import Pusher from 'pusher-js';
import * as Sentry from '@sentry/react';
import { API_URL } from './apiConstants';

const PUSHER_APP_KEY = 'b8c608fe1acb92d46ba6';
const APP_CLUSTER = 'us2';

// Connection states in which no real-time update can arrive.
const UNHEALTHY_CONNECTION_STATES = ['failed', 'unavailable'];

//singleton
let pusher = null;
let authToken = null;
const reportedConnectionStates = new Set();

export function setPusherAuthToken(token) {
	authToken = token;
}

/**
 * The live connection state ('connected', 'connecting', 'unavailable', 'failed', ...), or
 * 'uninitialized' before the first property page subscribes.
 */
export function getPusherConnectionState() {
	return pusher?.connection?.state || 'uninitialized';
}

/**
 * Warn (console + Sentry) once per page load per bad state. The client sat in 'failed' on
 * production for months with nothing logged, so real-time updates silently never arrived.
 */
function reportUnhealthyConnection(previous, current) {
	if (!UNHEALTHY_CONNECTION_STATES.includes(current) || reportedConnectionStates.has(current)) {
		return;
	}
	reportedConnectionStates.add(current);
	console.warn(`Pusher connection ${current} (was ${previous}); live updates fall back to polling`);
	Sentry.captureMessage(`Pusher connection ${current}`, {
		level: 'warning',
		extra: { previous, wsHost: pusher?.config?.wsHost },
	});
}

export function initPusher() {
	if (pusher === null) {

		//pusher 3rd party service version
		// pusher = new Pusher(PUSHER_APP_KEY, {
		// 	cluster: APP_CLUSTER,
		// });

		//socketi keith's diy version
		pusher = new Pusher(PUSHER_APP_KEY, {
			cluster: APP_CLUSTER,
			// wsHost: 'sockets.violerts.com',
			wsHost: 'sockets-ec2.violerts.com',
			wssPort: 443,
			// pusher-js only tries websockets when 'ws' is enabled; forceTLS upgrades it to wss.
			// ['wss'] alone leaves the client permanently 'failed'.
			enabledTransports: ['ws', 'wss'],
			disableStats: true,
			forceTLS: true,
			authorizer: (channel, options) => {
				return {
					authorize: (socketId, callback) => {
						// Get current authToken at time of authorization
						// Try to get from module variable first, then fallback to localStorage
						let currentToken = authToken;
						if (!currentToken) {
							currentToken = localStorage.getItem('authToken');
						}

						if (!currentToken) {
							const error = new Error('No authentication token available');
							callback(error, null);
							return;
						}

					const requestData = {
						socket_id: socketId,
						channel_name: channel.name,
					};

					fetch(`${API_URL}/api/broadcasting/auth`, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							'Authorization': `Bearer ${currentToken}`,
						},
						body: JSON.stringify(requestData),
					})
							.then(response => {
								if (!response.ok) {
									// Try to get error details from response
									return response.text().then(text => {
										let errorMessage = `Authorization failed: ${response.status}`;
										try {
											const errorData = JSON.parse(text);
											if (errorData.message) {
												errorMessage = errorData.message;
											}
										} catch (e) {
											// If not JSON, use the text or status
											if (text) {
												errorMessage = `${errorMessage} - ${text}`;
											}
										}
										throw new Error(errorMessage);
									});
								}
								return response.json();
							})
							.then(data => {
								callback(null, data);
							})
							.catch(error => {
								callback(error, null);
							});
					},
				};
			},
		});

		// Bind connection state changes AFTER Pusher is created
		if (pusher && pusher.connection) {
			pusher.connection.bind('state_change', ({ previous, current }) =>
				reportUnhealthyConnection(previous, current),
			);
			// The constructor may already have failed (e.g. no supported transport).
			reportUnhealthyConnection('initialized', pusher.connection.state);

			// Try to connect manually if not connecting
			setTimeout(() => {
				if (pusher.connection.state === 'disconnected' || pusher.connection.state === 'unavailable') {
					pusher.connect();
				}
			}, 1000);
		}

		// Force connection attempt
		if (pusher.connection.state === 'disconnected' || pusher.connection.state === 'unavailable') {
			pusher.connect();
		}
	} else {
		// Check if existing instance is connected
		if (pusher.connection.state === 'disconnected' || pusher.connection.state === 'unavailable') {
			pusher.connect();
		}
	}
}

export function unsubscribe(propertyId) {
	if (pusher && propertyId) {
		let channelName = `properties.${propertyId}`;
		pusher.unsubscribe(channelName);
		// console.log("unsubscribe: " + channelName);
	}
}

export function subscribe({ propertyId, onFirstLoadTaskComplete, onUpdate, onCountsUpdate }) {
	if (pusher === null) {

		initPusher();

		// pusher.connection.bind("error", function (error) {
		//   console.log('pusher error', error);
		// });
		// pusher.connection.bind('state_change', function(states) {
		// 	console.log('pusher states', states);
		// 	console.log('pusher states.previous', states.previous);
		// 	console.log('pusher states.current', states.current);
		// });
	}

	if (propertyId) {
		let channelName = `properties.${propertyId}`;
		let channel = pusher.subscribe(channelName);

		// console.log("Subscribed to updates: " + channelName);
		// console.log("channel: ", channel);

		if (typeof onFirstLoadTaskComplete === 'function') {
			channel.bind('App\\Events\\PropertyFirstLoadTaskComplete', onFirstLoadTaskComplete);
		}

		if (typeof onUpdate === 'function') {
			channel.bind('App\\Events\\PropertyDatasetUpdated', onUpdate);
		}

		if (typeof onCountsUpdate === 'function') {
			channel.bind('App\\Events\\PropertyCountsUpdated', onCountsUpdate);
		}
	}

}

export function unsubscribeFromConversation(conversationId) {
    if (pusher && conversationId) {
        const channelName = `private-conversations.${conversationId}`;
        pusher.unsubscribe(channelName);
    }
}

export function subscribeToConversation({ conversationId, onMessage }) {
    if (pusher === null) {
        initPusher();
    }

    if (!conversationId) {
        return () => {};
    }

    const channelName = `private-conversations.${conversationId}`;

    // Wait for connection before subscribing
    const subscribeWhenConnected = () => {
        if (pusher.connection.state === 'connected') {
            doSubscribe();
        } else {
            // Bind to connection event
            pusher.connection.bind('connected', () => {
                doSubscribe();
            });
        }
    };

    const doSubscribe = () => {
        const channel = pusher.subscribe(channelName);
        const eventName = 'App\\Events\\MessageSent';

        // Wait for subscription to succeed before binding events
        channel.bind('pusher:subscription_succeeded', () => {
            if (typeof onMessage === 'function') {
                channel.bind(eventName, (data) => {
                    onMessage(data);
                });
            }
        });

        // Also bind immediately in case subscription already succeeded
        if (typeof onMessage === 'function') {
            channel.bind(eventName, (data) => {
                onMessage(data);
            });
        }
    };

    // Start subscription process
    subscribeWhenConnected();

    // Return cleanup function
    return () => {
        if (pusher) {
            pusher.unsubscribe(channelName);
        }
    };
}
